import { uploadPrivateFile } from '@/lib/api/services/storage.service';
import { graphqlClient } from '@/lib/graphql-client';
import { postHrmApi } from '@/lib/hrm-api';
import { readWorkflowResponse } from '@/lib/workflow-webhook';
import {
  buildGetLeaveCategoriesLookupQuery,
  buildGetLeaveRequestsQuery,
  buildGetMyCurrentLeaveAllotmentsQuery,
} from '../graphql/queries';
import {
  GetLeaveRequestsParams,
  LeaveCategoriesLookupResult,
  LeaveRequestPreview,
  LeaveRequestsPagingResult,
  LeaveRequestSubmitWorkflowInput,
  LeaveRequestWithdrawWorkflowInput,
  LeaveWorkflowResult,
  MyCurrentLeaveAllotments,
  MyLeaveAllotment,
} from '../types/leave.types';

export const getLeaveRequests = async (
  params: GetLeaveRequestsParams
): Promise<{ LeaveRequests: LeaveRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetLeaveRequestsQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ LeaveRequests: LeaveRequestsPagingResult }>;
};

/** Leave categories of the active organization. */
export const getLeaveCategoriesLookup = async (): Promise<{
  LeaveCategorys: LeaveCategoriesLookupResult;
}> => {
  return graphqlClient.query({
    query: buildGetLeaveCategoriesLookupQuery({}, 1, 100),
  });
};

/**
 * The leave categories of the employee's current HR term (`IsCurrentHrYear`), like the legacy
 * `leave/detail/myallocations`: only these can be requested. None while no term is current.
 */
export const getMyCurrentLeaveAllotments = async (
  userId: string
): Promise<MyCurrentLeaveAllotments> => {
  const result = await graphqlClient.query<{
    HrProfiles: { items: { EmploymentType?: string; LeaveAllotments?: MyLeaveAllotment[] }[] };
  }>({
    query: buildGetMyCurrentLeaveAllotmentsQuery({ UserId: userId, IsCurrentHrYear: true }),
  });
  const term = result.HrProfiles.items[0];
  return { employmentType: term?.EmploymentType, allotments: term?.LeaveAllotments ?? [] };
};

/**
 * Uploads a sick leave's doctor's documents as Private files (medical data, read back through
 * short-lived signed URLs) and returns their ids.
 */
export const uploadLeaveDocuments = async (files: File[]): Promise<string[]> => {
  const uploaded = await Promise.all(files.map((file) => uploadPrivateFile(file)));
  const fileIds = uploaded.map(({ fileId }) => fileId);
  if (fileIds.some((fileId) => !fileId)) throw new Error('LEAVE_DOCUMENT_UPLOAD_FAILED');
  return fileIds;
};

/**
 * Submits a leave request through the LEAVE_REQUEST_SUBMIT workflow, which validates the input,
 * checks the caller's `hrm::time-off::leave-request` permission and the leave rule catalog (notice period, max
 * consecutive days, HR-period crossing, the sick leave document), stamps the employee's department and
 * stores the day-only dates. A refusal comes back as `IsSuccessful: false` with the workflow's message.
 */
export const submitLeaveRequest = async (
  input: LeaveRequestSubmitWorkflowInput
): Promise<LeaveWorkflowResult> => {
  const response = await postHrmApi('LEAVE_REQUEST_SUBMIT', { ...input });
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'LEAVE_REQUEST_SUBMIT_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};

/**
 * What a request would take (working days, hours, and the overtime left for an "Overtime Leave"), from
 * the LEAVE_REQUEST_SUBMIT workflow itself (`preview: true`, nothing is written), so the form never shows
 * a number the request would not save. `null` when the workflow refuses (e.g. no permission).
 */
export const previewLeaveRequest = async (
  input: Omit<LeaveRequestSubmitWorkflowInput, 'preview' | 'reason'>
): Promise<LeaveRequestPreview | null> => {
  const response = await postHrmApi('LEAVE_REQUEST_SUBMIT', { ...input, preview: true });
  const result = readWorkflowResponse<LeaveRequestPreview>(response, 'LEAVE_REQUEST_SUBMIT_FAILED');
  return result.IsSuccessful && result.Data ? result.Data : null;
};

/** Withdraws the caller's own pending leave request through the LEAVE_REQUEST_WITHDRAW workflow. */
export const withdrawLeaveRequest = async (itemId: string): Promise<LeaveWorkflowResult> => {
  const input: LeaveRequestWithdrawWorkflowInput = { itemId };
  const response = await postHrmApi('LEAVE_REQUEST_WITHDRAW', { ...input });
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'LEAVE_REQUEST_WITHDRAW_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};
